import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useScoring } from "@/hooks/useScoring";
import { useCompleteSetup } from "@/hooks/useSetup";
import apiClient from "@/lib/api-client";
import { trackEvent } from "@/lib/engagement";
import { ensureNetworks } from "@/lib/setup/ensureNetworks";
import { shortTitle } from "@/lib/setup/grades";
import { inYearRange } from "@/lib/setup/preview";
import { networkToPapers, postBatches, type RawTrawledPaper } from "@/lib/trawl-utils";
import { useScoringStore } from "@/stores/scoring-store";
import { useSeedDiscoveryStore } from "@/stores/seed-discovery-store";
import { seedKey } from "@/stores/setup-store";
import { useQuickStartStore } from "@/stores/useQuickStartStore";
import type { Concept } from "@/types/concept";
import type { DiscoveryRelation, PaperListResponse } from "@/types/paper";

/** `done` = setup complete (④ then waits for Open Curate; no auto-redirect). */
export type StartState = "idle" | "starting" | "done" | "error" | "no_network";
/** What Start is doing while `state === "starting"` (60–80 s on a big setup). */
export type StartProgress =
  | { phase: "networks"; seeds: number }
  | { phase: "saving"; papers: number }
  | { phase: "finishing" };

/** A just-saved paper and how it hangs off a seed ("cites <seed>"). */
export interface BuildLink { title: string; relation: string }

/** What ④ shows while the build runs. Counts are papers *sent* (client-side
 *  deduped); the server may report some as duplicates/merged. */
export interface BuildStats {
  seeds: number;
  seedsDone: number;            // networks settled so far
  connected: number | null;     // unique papers across seeds, all years
  inYears: number | null;
  hidden: number | null;        // connected - inYears
  savedInYears: number;         // in-years papers in batches saved so far
  recent: BuildLink[];          // ≤5 in-years papers from the latest saved batch
  completedAt: number | null;
  scoringStarted: boolean;      // this run called startScoring
  scoringQueued: boolean;       // ...while another job held the global store
}

const initialStats: BuildStats = {
  seeds: 0, seedsDone: 0, connected: null, inYears: null, hidden: null,
  savedInYears: 0, recent: [], completedAt: null, scoringStarted: false, scoringQueued: false,
};

const RELATION_TEXT: Record<DiscoveryRelation, string> = {
  cites_parent: "cites", cited_by_parent: "cited by", recommended_with: "related to", cocited_with: "related to",
};

function relationLine(p: RawTrawledPaper, seedTitles: Map<string, string>): string {
  const link = p.discovered_via?.[0];
  const seed = link && seedTitles.get(link.parent_paper_id);
  const verb = link && RELATION_TEXT[link.relation];
  return seed && verb ? `${verb} ${shortTitle(seed)}` : "linked to a seed";
}

export function useStartSetup(concept: Concept) {
  const queryClient = useQueryClient();
  const complete = useCompleteSetup(concept.id);
  const { startScoring } = useScoring(concept.id);
  const [state, setState] = useState<StartState>("idle");
  const [progress, setProgress] = useState<StartProgress | null>(null);
  const [stats, setStats] = useState<BuildStats>(initialStats);

  async function start() {
    setState("starting");
    setProgress(null);
    setStats(initialStats); // Try again must not inflate counts
    try {
      // The saved rows are the chosen seeds (③'s Next synced them). Start adds
      // exactly what the preview showed: seeds it settled are reused, not
      // refetched; only seeds it never settled (e.g. after a reload) fetch.
      const { papers: rows } = await apiClient
        .get(`api/concepts/${concept.id}/papers`, { searchParams: { role: "foundational", per_page: "500" } })
        .json<PaperListResponse>();
      const seeds = rows.map((r) => ({ key: seedKey(r), paper_id: r.paper_id, doi: r.doi, title: r.title }));
      setProgress({ phase: "networks", seeds: seeds.length });
      setStats((s) => ({ ...s, seeds: seeds.length }));
      const onSettled = () => setStats((s) => ({ ...s, seedsDone: s.seedsDone + 1 }));
      let ensured = await ensureNetworks(seeds, concept.scope?.recency, { retryFailed: false, onSettled });
      // Nothing to reuse (S2 was down for every seed): there's no preview to
      // stay consistent with, so "Try again" really asks S2 again.
      if (!ensured.some((e) => e.network)) {
        setStats((s) => ({ ...s, seedsDone: 0 }));
        ensured = await ensureNetworks(seeds, concept.scope?.recency, { retryFailed: true, onSettled });
      }
      const items = ensured.flatMap(({ network }, i) =>
        network ? [{ network, parent: { paperId: rows[i].id, method: "trawl" as const } }] : [],
      );
      if (!items.length) {
        // Nothing to trawl: don't finish a setup that would land on an empty Curate.
        setState("no_network");
        return;
      }
      const papers = networkToPapers(items);
      const inYears = (p: RawTrawledPaper) => inYearRange(p.year, concept.scope);
      const nIn = papers.filter(inYears).length;
      setStats((s) => ({ ...s, connected: papers.length, inYears: nIn, hidden: papers.length - nIn }));
      const seedTitles = new Map(rows.map((r) => [r.id, r.title]));
      const onBatch = (batch: RawTrawledPaper[]) => {
        const landed = batch.filter(inYears);
        setStats((s) => ({
          ...s,
          savedInYears: s.savedInYears + landed.length,
          recent: landed.slice(0, 5).map((p) => ({ title: p.title, relation: relationLine(p, seedTitles) })),
        }));
      };
      setProgress({ phase: "saving", papers: papers.length });
      if (papers.length) await postBatches(concept.id, papers, new AbortController().signal, "trawl", onBatch);
      setProgress({ phase: "finishing" });
      const done = await complete.mutateAsync();
      setStats((s) => ({ ...s, completedAt: Date.now() }));
      // Seed the cache with the completed concept so Curate's unfinished-setup
      // redirect doesn't bounce us back off a stale entry (onSettled still
      // invalidates the list).
      queryClient.setQueryData(["concepts", concept.id], done);
      useSeedDiscoveryStore.getState().reset(); // ③'s candidates must not pop over Curate
      useQuickStartStore.getState().setSetupJustCompleted(true);
      // The scoring store is global with no concept id: clear a previous run's
      // leftovers so ④ reads only this run — unless another job is mid-run, in
      // which case ④ can't tell the two apart and must not read its counts.
      const score = (opts: Parameters<typeof startScoring>[0]) => {
        const queued = useScoringStore.getState().isScoring;
        if (!queued) useScoringStore.getState().resetScoring();
        setStats((s) => ({ ...s, scoringStarted: true, scoringQueued: queued }));
        void startScoring(opts);
      };
      try {
        await queryClient.invalidateQueries({ queryKey: ["papers", concept.id] });
        await queryClient.invalidateQueries({ queryKey: ["paper-keys", concept.id] });
        const trawled = await apiClient
          .get(`api/concepts/${concept.id}/papers`, { searchParams: { role: "trawled", per_page: "5000" } })
          .json<PaperListResponse>();
        // scoring_timestamp, not relevance_score === 0: a genuine 0 from a partial
        // earlier run must not be rescored (explicit paper_ids bypass unscored_only).
        const unscored = trawled.papers.filter((p) => p.scoring_timestamp == null && !p.exclusion_reason).map((p) => p.id);
        trackEvent("setup_step_completed", {
          step: 4, concept_id: concept.id, posted: papers.length,
          seeds: rows.length, seeds_without_network: rows.length - items.length, flow: "boards",
        });
        // Not awaited: scoring streams into the global scoring store Curate renders.
        if (unscored.length) score({ paperIds: unscored, interactive: true });
      } catch {
        // Setup is already complete: don't strand the user. Without paperIds the
        // backend scores unscored papers only.
        score({ interactive: true });
      }
      setState("done");
    } catch {
      setState("error");
    }
  }

  return { start, state, progress, stats };
}
